import { ChangeDetectionStrategy, Component, EventEmitter, Output } from '@angular/core';
import { untranslatedString } from 'models/translated-string';
import { AlertController, AlertButtonRole } from 'providers/alert-controller';
import { PopoverController } from 'providers/popover-controller';
import { CsLogDetailPopover } from 'components/cs-log-detail-popover/cs-log-detail-popover';
import { CsViewNotesPopover } from 'components/cs-view-notes-popover/cs-view-notes-popover';
import { SAMPLE_LOG_DETAIL_SETS } from 'pages/test-ui-components/cs-test-content-card';

// The Alert Tests card's body on the Test UI Components page: buttons that open every kind of
// cs-alert, the popovers opened through their own present() helpers, and the screenshot annotator.
// The annotator lays itself out as a whole page, so the page shows it over itself; this card only
// asks for it through annotatorRequested.
@Component({
  selector: 'cs-test-alerts-card',
  host: { class: 'showcase-card-body' },
  templateUrl: 'cs-test-alerts-card.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CsTestAlertsCardComponent {
  // the Screenshot Annotator button was tapped
  @Output() annotatorRequested = new EventEmitter<void>();

  constructor(
    private alertCtrl: AlertController,
    private popoverCtrl: PopoverController,
  ) {}

  // Test alert methods
  async showSimpleAlert() {
    await this.alertCtrl.showAlertAsync({
      title: untranslatedString('Simple Alert'),
      message: untranslatedString('This is a simple alert with one button.'),
      buttons: [
        {
          text: untranslatedString('OK'), role: AlertButtonRole.Primary
        }
      ]
    });
  }

  async showConfirmAlert() {
    await this.alertCtrl.showAlertAsync({
      title: untranslatedString('Confirm Action'),
      message: untranslatedString('Are you sure you want to proceed with this action?'),
      buttons: [
        {
          text: untranslatedString('Cancel'),
          role: AlertButtonRole.Cancel
        },
        {
          text: untranslatedString('Confirm'),
          role: AlertButtonRole.Primary,
          result: () => {
            console.log('Confirmed!');
            return 'confirmed';
          }
        }
      ]
    });
  }

  async showRadioAlert() {
    await this.alertCtrl.showAlertAsync({
      title: untranslatedString('Select Option'),
      message: untranslatedString('Choose one of the following options:'),
      inputs: [
        {
          type: 'radio',
          label: untranslatedString('Option 1'),
          value: 'option1',
          checked: true
        },
        {
          type: 'radio',
          label: untranslatedString('Option 2'),
          value: 'option2'
        },
        {
          type: 'radio',
          label: untranslatedString('Option 3'),
          value: 'option3'
        }
      ],
      buttons: [
        {
          text: untranslatedString('Cancel'),
          role: AlertButtonRole.Cancel
        },
        {
          text: untranslatedString('OK'),
          role: AlertButtonRole.Primary,
          result: (data: any) => {
            console.log('Selected:', data);
            return data;
          }
        }
      ]
    });
  }

  async showInputAlert() {
    await this.alertCtrl.showAlertAsync({
      title: untranslatedString('Enter Information'),
      inputs: [
        {
          name: 'name',
          type: 'text',
          placeholder: untranslatedString('Your name')
        },
        {
          name: 'email',
          type: 'email',
          placeholder: untranslatedString('Your email')
        },
        // the alert's other two field kinds, so every control inside cs-alert can be inspected (#6020)
        {
          name: 'time',
          type: 'time',
          value: '07:30'
        },
        {
          name: 'size',
          type: 'select',
          value: 'medium',
          selectOptions: [
            { label: untranslatedString('Small'), value: 'small' },
            { label: untranslatedString('Medium'), value: 'medium' },
            { label: untranslatedString('Large'), value: 'large' }
          ]
        }
      ],
      buttons: [
        {
          text: untranslatedString('Submit'),
          role: AlertButtonRole.Primary,
          result: (data: any) => {
            console.log('Form data:', data);
            return data;
          }
        },
        {
          text: untranslatedString('Cancel'),
          role: AlertButtonRole.Cancel
        }
      ]
    });
  }

  async showLongTextAlert() {
    await this.alertCtrl.showAlertAsync({
      title: untranslatedString('Terms and Conditions'),
      message: untranslatedString('Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.'),
      buttons: [
        {
          text: untranslatedString('Decline'),
          role: AlertButtonRole.Cancel
        },
        {
          text: untranslatedString('Accept'),
          role: AlertButtonRole.Primary
        }
      ]
    });
  }

  async showThreeButtonAlert() {
    await this.alertCtrl.showAlertAsync({
      title: untranslatedString('Save Changes?'),
      message: untranslatedString('You have unsaved changes. What would you like to do?'),
      buttons: [
        {
          text: untranslatedString("Don't Save"),
        },
        {
          text: untranslatedString('Cancel'),
          role: AlertButtonRole.Cancel
        },
        {
          text: untranslatedString('Save'),
          role: AlertButtonRole.Primary,
          result: () => {
            console.log('Saving...');
            return 'saved';
          }
        }
      ]
    });
  }

  async showNonDismissibleAlert() {
    await this.alertCtrl.showAlertAsync({
      title: untranslatedString('Non-Dismissible Alert'),
      message: untranslatedString('This alert cannot be dismissed by clicking outside or with the close button. You must choose an option. Notice there is no close button.'),
      enableBackdropDismiss: false,
      buttons: [
        {
          text: untranslatedString('Option 1'),
          role: AlertButtonRole.Primary,
          result: () => {
            console.log('Selected Option 1');
            return 'option1';
          }
        },
        {
          text: untranslatedString('Option 2'),
          role: AlertButtonRole.Cancel,
          result: () => {
            console.log('Selected Option 2');
            return 'option2';
          }
        }
      ]
    });
  }

  async showLongButtonTextAlert() {
    await this.alertCtrl.showAlertAsync({
      title: untranslatedString('You changed the weight?'),
      message: untranslatedString("Our app estimates the ideal load for each of your working sets. Why do you feel we got it wrong?"),
      buttons: [
        {
          text: untranslatedString("My chosen weight is the closest I can get to what you are telling me to use"),
          role: AlertButtonRole.Primary,
          result: () => {
            console.log('User selected closest weight option');
            return 'closest';
          }
        },
        {
          text: untranslatedString("The weight you are telling me to use is way off"),
          role: AlertButtonRole.Cancel,
          result: () => {
            console.log('User selected way off option');
            return 'way_off';
          }
        }
      ]
    });
  }

  async showExplicitlyDismissibleAlert() {
    await this.alertCtrl.showAlertAsync({
      title: untranslatedString('Explicitly Dismissible Alert'),
      message: untranslatedString('This alert explicitly sets enableBackdropDismiss to true. It can be dismissed by clicking outside or using the close button.'),
      enableBackdropDismiss: true,
      buttons: [
        {
          text: untranslatedString('OK'),
          role: AlertButtonRole.Primary
        }
      ]
    });
  }

  // Popovers opened by their own present() helpers (#6020)
  // opens the last-workout detail popover with sample sets, as the workout page does
  async showLogDetailPopover(event: MouseEvent) {
    await CsLogDetailPopover.present(this.popoverCtrl, event.target as HTMLElement, {
      dateTitle: 'Last Workout - Wednesday, August 12',
      subtitle: 'Same intensity level',
      sets: SAMPLE_LOG_DETAIL_SETS,
    });
  }

  // opens the pinned-note popover with a sample note, as the workout page does
  async showViewNotesPopover(event: MouseEvent) {
    await CsViewNotesPopover.present(this.popoverCtrl, event.target as HTMLElement, {
      exerciseName: 'Bench Press',
      note: 'Keep the shoulder blades pinched and pause each rep on the chest.',
    });
  }
}
